import {
  collection,
  doc,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
  type CollectionReference,
  type DocumentData,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { StreamInterface } from 'tricklejs/dist/types'
import { ZodError } from 'zod'
import { addDocWithError, deleteDocWithError } from '../../firestore-mobx/fetch'
import { modelListStream } from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { GradeSyncJob } from '../../models/GradeSyncJob'
import type { FirestoreGradeSyncJob } from './schema'
import { GradeSyncJobStatus, schema } from './schema'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreGradeSyncJob> = {
  toFirestore: (data) => data,
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    try {
      return schema.parse(data)
    } catch (e) {
      if (e instanceof ZodError) {
        console.error('Error parsing grade sync job', e.errors)
      }
      throw e
    }
  },
}

const getColRef = (
  firestore: Firestore,
  userId: string
): CollectionReference<FirestoreGradeSyncJob, DocumentData> => {
  return collection(
    firestore,
    'user_profile',
    userId,
    'grade_sync_job'
  ).withConverter(converter)
}

export const createGradeSyncJob = async (
  repository: FirebaseRepository,
  params: {
    sectionId: string
    assignmentId: string
    roomStateId?: string
    targetUserId?: string
    forceResend: boolean
  }
): Promise<string> => {
  const uid = repository.uid
  const ref = getColRef(repository.firestore, uid)

  const docRef = await addDocWithError(
    ref,
    {
      sectionId: params.sectionId,
      assignmentId: params.assignmentId,
      roomStateId: params.roomStateId,
      targetUserId: params.targetUserId,
      forceResend: params.forceResend,
      status: GradeSyncJobStatus.pending,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    'CreateGradeSyncJobError'
  )

  return docRef.id
}

export const getActiveGradeSyncJobs = (
  repository: FirebaseRepository,
  userId: string
): StreamInterface<GradeSyncJob[]> => {
  const ref = getColRef(repository.firestore, userId)

  // Get jobs that are pending, running, continuing, or recently completed - we fetch last 20
  // We include completed/failed so the frontend can show the toast
  const q = query(
    ref,
    where('status', 'in', [
      GradeSyncJobStatus.pending,
      GradeSyncJobStatus.running,
      GradeSyncJobStatus.continuing,
      GradeSyncJobStatus.completed,
      GradeSyncJobStatus.failed,
    ]),
    orderBy('createdAt', 'desc'),
    // only get the latest 20 jobs - prevents the collection from growing too large
    limit(20)
  )

  return modelListStream(repository, q, GradeSyncJob, {
    // Permission errors are expected here during sign-out / when the auth
    // state changes while the stream is still subscribed. Don't report them
    // to Sentry — the stream is scoped to the current user and will be
    // recreated after the user signs back in.
    ignorePermissionErrors: true,
  })
}

export const deleteGradeSyncJob = async (
  repository: FirebaseRepository,
  userId: string,
  jobId: string
): Promise<void> => {
  const ref = getColRef(repository.firestore, userId)
  const docRef = doc(ref, jobId)
  await deleteDocWithError(docRef, 'DeleteGradeSyncJobError')
}
